import React, { useEffect, useState } from 'react';
import { Target, Check, Edit2, MessageSquare, Archive, ArchiveRestore, Trash2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

/**
 * FocusPromptList — renders coach-assigned focus prompts with a 7-day completion grid.
 *
 * Modes:
 *  - mode="player"  : show prompts only (active), let the player tick today + add an optional note.
 *  - mode="coach"   : let the coach create/edit/archive/delete prompts for a specific player.
 *
 * Props:
 *  - mode: 'player' | 'coach' (default 'player')
 *  - playerId: required when mode === 'coach' (the student id)
 *  - title, subtitle, compact (boolean), includeArchived (coach only, default true)
 *  - onChange: optional callback fired after any mutation, receives the latest list
 */
const dayLabel = (iso) => {
  // 2-letter weekday so Tue/Thu and Sat/Sun don't collide
  const wd = new Date(iso + 'T00:00:00Z').toLocaleDateString('en-GB', { weekday: 'short' });
  return wd.slice(0, 2);
};

const GridDots = ({ grid, color = '#2C5530' }) => (
  <div className="flex items-center gap-1.5" data-testid="focus-grid">
    {grid.map((g) => (
      <div key={g.date} className="flex flex-col items-center" title={`${g.date}${g.done ? ' — done' : ''}`}>
        <span
          className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
            g.done ? 'text-white' : 'bg-[#F2F0E9] text-[#A8A29E]'
          }`}
          style={g.done ? { backgroundColor: color } : {}}
          data-testid={`grid-dot-${g.date}`}
        >
          {g.done ? <Check className="w-3 h-3" /> : ''}
        </span>
        <span className="text-[9px] uppercase tracking-wider text-[#A8A29E] mt-0.5">{dayLabel(g.date)}</span>
      </div>
    ))}
  </div>
);

const PlayerPromptRow = ({ prompt, onToggle }) => {
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState(prompt.today_note || '');
  const [busy, setBusy] = useState(false);

  const tick = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/locker/focus/${prompt.id}/complete`, {});
      onToggle(data);
      toast.success(data.today_done ? 'Marked done for today.' : 'Unticked.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  const saveNote = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/locker/focus/${prompt.id}/complete`, { note });
      onToggle(data);
      toast.success('Saved.');
      setShowNote(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E7E5E4] p-4" data-testid={`focus-prompt-${prompt.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <button
          onClick={tick}
          disabled={busy}
          aria-pressed={prompt.today_done}
          className={`flex-shrink-0 w-7 h-7 border-2 transition-colors flex items-center justify-center mt-0.5 ${
            prompt.today_done
              ? 'bg-[#2C5530] border-[#2C5530] text-white'
              : 'border-[#A8A29E] hover:border-[#2C5530] text-transparent hover:text-[#2C5530]'
          }`}
          data-testid={`focus-toggle-${prompt.id}`}
          title={prompt.today_done ? 'Tick today off' : 'Mark today as done'}
        >
          <Check className="w-4 h-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className={`text-sm leading-snug ${prompt.today_done ? 'text-[#737373] line-through decoration-[#A8A29E]/60' : 'text-[#1A1A1A]'}`}>
            {prompt.text}
          </p>
          <div className="flex items-center gap-2 mt-1.5 text-[10px] uppercase tracking-wider text-[#A8A29E]">
            <span>From {prompt.coach_name || 'Coach'}</span>
            <span>·</span>
            <span>{prompt.completions_last_7}/7 days</span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <GridDots grid={prompt.grid} />
        {prompt.today_done && (
          <button
            onClick={() => { setShowNote(!showNote); setNote(prompt.today_note || ''); }}
            className="text-xs text-[#737373] hover:text-[#1A2E1D] inline-flex items-center gap-1"
            data-testid={`focus-note-toggle-${prompt.id}`}
          >
            <MessageSquare className="w-3 h-3" /> {prompt.today_note ? 'Edit note' : 'Add note'}
          </button>
        )}
      </div>
      {showNote && (
        <div className="mt-3 bg-[#FAFAF9] border border-[#E7E5E4] p-2.5" data-testid={`focus-note-form-${prompt.id}`}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="A quick note for future-you (optional)…"
            rows={2}
            maxLength={500}
            className="w-full text-xs bg-white border border-[#E7E5E4] p-2 focus:outline-none focus:border-[#1A2E1D]"
            data-testid={`focus-note-input-${prompt.id}`}
          />
          <div className="flex justify-end gap-2 mt-1.5">
            <button onClick={() => setShowNote(false)} className="text-xs text-[#737373] px-2 py-1 hover:text-[#1A1A1A]">Cancel</button>
            <button
              onClick={saveNote}
              disabled={busy}
              className="text-xs bg-[#1A2E1D] text-white font-semibold px-3 py-1.5 hover:bg-[#0F1F12] disabled:opacity-50"
              data-testid={`focus-note-save-${prompt.id}`}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CoachPromptRow = ({ prompt, onChange, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(prompt.text);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const v = text.trim();
    if (!v) return;
    setBusy(true);
    try {
      const { data } = await API.patch(`/coach/locker/focus/${prompt.id}`, { text: v });
      onChange(data);
      setEditing(false);
      toast.success('Updated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  const toggleArchive = async () => {
    setBusy(true);
    try {
      const { data } = await API.patch(`/coach/locker/focus/${prompt.id}`, { archived: !prompt.archived_at });
      onChange(data);
      toast.success(prompt.archived_at ? 'Re-activated.' : 'Archived.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this focus prompt and all its completions? Cannot be undone.')) return;
    setBusy(true);
    try {
      await API.delete(`/coach/locker/focus/${prompt.id}`);
      onDelete(prompt.id);
      toast.success('Deleted.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`border p-4 ${prompt.archived_at ? 'bg-[#FAFAF9] border-dashed border-[#E7E5E4] opacity-75' : 'bg-white border-[#E7E5E4]'}`}
      data-testid={`coach-focus-prompt-${prompt.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={2}
                maxLength={280}
                className="w-full text-sm bg-white border border-[#E7E5E4] p-2 focus:outline-none focus:border-[#1A2E1D]"
                data-testid={`coach-focus-edit-${prompt.id}`}
              />
              <div className="flex justify-end gap-2 mt-2">
                <button onClick={() => { setEditing(false); setText(prompt.text); }} className="text-xs text-[#737373] px-2 py-1 hover:text-[#1A1A1A]">Cancel</button>
                <button onClick={save} disabled={busy} className="text-xs bg-[#1A2E1D] text-white font-semibold px-3 py-1.5 hover:bg-[#0F1F12] disabled:opacity-50" data-testid={`coach-focus-save-${prompt.id}`}>Save</button>
              </div>
            </div>
          ) : (
            <p className={`text-sm leading-snug ${prompt.archived_at ? 'text-[#A8A29E] line-through' : 'text-[#1A1A1A]'}`}>{prompt.text}</p>
          )}
          <div className="flex items-center gap-2 mt-1.5 text-[10px] uppercase tracking-wider text-[#A8A29E] flex-wrap">
            <span>{prompt.completions_last_7}/7 this week</span>
            <span>·</span>
            <span>{prompt.completions_total} all time</span>
            {prompt.archived_at && (<><span>·</span><span className="text-[#BC562B]">archived</span></>)}
          </div>
        </div>
        {!editing && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => setEditing(true)} disabled={!!prompt.archived_at} className="p-1.5 text-[#737373] hover:text-[#1A2E1D] disabled:opacity-30" title="Edit" data-testid={`coach-focus-edit-btn-${prompt.id}`}>
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={toggleArchive} disabled={busy} className="p-1.5 text-[#737373] hover:text-[#BC562B]" title={prompt.archived_at ? 'Re-activate' : 'Archive'} data-testid={`coach-focus-archive-btn-${prompt.id}`}>
              {prompt.archived_at ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
            </button>
            <button onClick={remove} disabled={busy} className="p-1.5 text-[#BC562B] hover:text-[#8B1E1E]" title="Delete" data-testid={`coach-focus-delete-btn-${prompt.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      {/* Full 7-day check grid + notes */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <GridDots grid={prompt.grid} color="#2C5530" />
      </div>
      {prompt.grid.some((g) => g.note) && (
        <ul className="mt-3 space-y-1 border-t border-[#F2F0E9] pt-2" data-testid={`coach-focus-notes-${prompt.id}`}>
          {prompt.grid.filter((g) => g.note).map((g) => (
            <li key={g.date} className="text-xs">
              <span className="text-[#A8A29E] mr-2">{new Date(g.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
              <span className="text-[#57534E] whitespace-pre-wrap">"{g.note}"</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FocusPromptList = ({ mode = 'player', playerId, title, subtitle, includeArchived = true, onChange }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    try {
      const url = mode === 'coach'
        ? `/coach/locker/students/${playerId}/focus?include_archived=${includeArchived}`
        : '/locker/focus';
      const { data } = await API.get(url);
      setPrompts(data);
      onChange && onChange(data);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [playerId, mode, includeArchived]);

  const handleChange = (updated) => {
    const next = prompts.map((p) => (p.id === updated.id ? updated : p));
    setPrompts(next);
    onChange && onChange(next);
  };

  const handleDelete = (id) => {
    const next = prompts.filter((p) => p.id !== id);
    setPrompts(next);
    onChange && onChange(next);
  };

  const handleAdd = async () => {
    const v = newText.trim();
    if (!v) return;
    setAdding(true);
    try {
      const { data } = await API.post(`/coach/locker/students/${playerId}/focus`, { text: v });
      const next = [...prompts, data];
      setPrompts(next);
      onChange && onChange(next);
      setNewText('');
      setShowAdd(false);
      toast.success('Focus prompt sent to player.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add.');
    } finally {
      setAdding(false);
    }
  };

  const activeCount = prompts.filter((p) => !p.archived_at).length;
  const visible = mode === 'coach' ? prompts : prompts.filter((p) => !p.archived_at);

  if (loading) {
    return <div className="h-12 flex items-center text-xs text-[#A8A29E]" data-testid="focus-loading">Loading focus prompts…</div>;
  }

  if (mode === 'player' && visible.length === 0) return null; // hide panel completely for players with nothing

  return (
    <div className="bg-white border border-[#E7E5E4] p-5" data-testid={`focus-list-${mode}`}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Target className="w-4 h-4 text-[#BC562B] flex-shrink-0" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#BC562B]">
              {mode === 'coach' ? 'Coach view · Daily focus' : 'From your coach'}
            </p>
            <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A]">{title || 'Daily focus'}</h3>
            {subtitle && <p className="text-xs text-[#57534E]">{subtitle}</p>}
          </div>
        </div>
        {mode === 'coach' && (
          <button
            onClick={() => setShowAdd(!showAdd)}
            disabled={activeCount >= 3}
            className="inline-flex items-center gap-1.5 bg-[#2C5530] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#1E3A21] disabled:opacity-50"
            data-testid="coach-focus-add-toggle"
            title={activeCount >= 3 ? 'Max 3 active prompts — archive one to add another.' : 'New focus prompt'}
          >
            {showAdd ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            {showAdd ? 'Cancel' : `New prompt (${activeCount}/3)`}
          </button>
        )}
      </div>

      {mode === 'coach' && showAdd && (
        <div className="bg-[#FAFAF9] border border-[#BC562B] p-3 mb-4" data-testid="coach-focus-new-form">
          <textarea
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            rows={2}
            maxLength={280}
            placeholder="e.g. Stick to a 3-breath pre-shot reset on every approach"
            className="w-full text-sm bg-white border border-[#E7E5E4] p-2 focus:outline-none focus:border-[#1A2E1D]"
            data-testid="coach-focus-new-input"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-[#A8A29E]">{newText.length}/280</span>
            <button
              onClick={handleAdd}
              disabled={adding || !newText.trim()}
              className="inline-flex items-center gap-1.5 bg-[#1A2E1D] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#0F1F12] disabled:opacity-50"
              data-testid="coach-focus-new-save"
            >
              <Plus className="w-3.5 h-3.5" /> {adding ? 'Sending…' : 'Send to player'}
            </button>
          </div>
        </div>
      )}

      {visible.length === 0 ? (
        <p className="text-sm text-[#A8A29E]" data-testid="focus-empty">
          {mode === 'coach' ? 'No focus prompts yet. Send one to nudge a daily intent.' : 'No active focus prompts.'}
        </p>
      ) : (
        <ul className="space-y-3">
          {visible.map((p) => (
            <li key={p.id}>
              {mode === 'coach'
                ? <CoachPromptRow prompt={p} onChange={handleChange} onDelete={handleDelete} />
                : <PlayerPromptRow prompt={p} onToggle={handleChange} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FocusPromptList;
